import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type { TasteConfig, TasteStep, ConditionRule } from '../types/taste-config'
import { fetchTasteConfig, fetchTasteConfigWithResume, submitTasteAnswers } from '../api/taste'
import type { TasteSubmitRequest } from '../api/taste'
import type { TasteResults } from '../types/taste-config'
import type { UserProfileDataResponse } from '@/types/profile-data'
import { queryCache } from '@/queries/core/queryCache'

const TASTE_PROGRESS_KEY = 'fair_taste_progress'

function saveTasteProgress(configId: string, tasteData: Record<string, any>, stepIndex: number) {
  try {
    localStorage.setItem(TASTE_PROGRESS_KEY, JSON.stringify({ configId, tasteData, stepIndex }))
  } catch {
    // localStorage may be unavailable
  }
}

function loadTasteProgress(configId: string): { tasteData: Record<string, any>; stepIndex: number } | null {
  try {
    const raw = localStorage.getItem(TASTE_PROGRESS_KEY)
    if (!raw) return null
    const data = JSON.parse(raw)
    if (data.configId !== configId) return null
    return { tasteData: data.tasteData, stepIndex: data.stepIndex }
  } catch {
    return null
  }
}

function clearTasteProgress() {
  try {
    localStorage.removeItem(TASTE_PROGRESS_KEY)
  } catch {
    // localStorage may be unavailable
  }
}

export const useTasteClarificationModal = defineStore('tasteClarificationModal', () => {
  const tasteModal = ref({
    isShow: false,
    currentStepIndex: 0,
    isLoading: false,
    isLoadingConfig: false,
    config: null as TasteConfig | null,
    tasteData: {} as Record<string, any>,
    results: null as TasteResults | null,
    sessionId: null as string | null,
    isShowingLaterStep: false,
    previousStepIndex: null as number | null
  })

  const steps = computed(() => tasteModal.value.config?.steps ?? [])

  // Создаем статический шаг "Позже"
  const laterStep: TasteStep = {
    id: 'later-step',
    type: 'later',
    title: 'Отличное начало!',
    subtitle: 'Основная информация о вашем стиле уже сохранена.\n\nДля полной персонализации вы сможете оценить образы позже — в вашем личном кабинете доступен весь опрос.',
    required: false
  }

  const currentStep = computed(() => {
    if (tasteModal.value.isShowingLaterStep) {
      return laterStep
    }
    return steps.value[tasteModal.value.currentStepIndex]
  })
  const totalSteps = computed(() => steps.value.length)

  const getCurrentStep = (): TasteStep | undefined => {
    return currentStep.value
  }

  const getStepById = (stepId: string): TasteStep | undefined => {
    return steps.value.find(step => step.id === stepId)
  }

  const getStepIndexById = (stepId: string): number => {
    return steps.value.findIndex(step => step.id === stepId)
  }

  const evaluateConditionRule = (rule: ConditionRule, tasteData: Record<string, any>): boolean => {
    const fieldValue = tasteData[rule.field]

    switch (rule.operator) {
      case 'equals':
        return fieldValue === rule.value
      case 'not_equals':
        return fieldValue !== rule.value
      case 'greater_than':
        return typeof fieldValue === 'number' && typeof rule.value === 'number' && fieldValue > rule.value
      case 'less_than':
        return typeof fieldValue === 'number' && typeof rule.value === 'number' && fieldValue < rule.value
      case 'greater_or_equal':
        return typeof fieldValue === 'number' && typeof rule.value === 'number' && fieldValue >= rule.value
      case 'less_or_equal':
        return typeof fieldValue === 'number' && typeof rule.value === 'number' && fieldValue <= rule.value
      case 'in':
        return Array.isArray(rule.value) && rule.value.includes(fieldValue)
      case 'not_in':
        return Array.isArray(rule.value) && !rule.value.includes(fieldValue)
      case 'exists':
        return fieldValue !== null && fieldValue !== undefined && fieldValue !== ''
      case 'not_exists':
        return fieldValue === null || fieldValue === undefined || fieldValue === ''
      default:
        return false
    }
  }

  const shouldStepBeVisible = (step: TasteStep): boolean => {
    if (!step.conditional) return true

    const condition = step.conditional
    const data = tasteModal.value.tasteData

    // Поддержка старого формата для обратной совместимости
    if (condition.field && condition.value !== undefined) {
      return data[condition.field] === condition.value
    }

    // Новый формат с правилами
    if (condition.rules && condition.rules.length > 0) {
      const logic = condition.logic || 'AND'

      if (logic === 'AND') {
        return condition.rules.every(rule => evaluateConditionRule(rule, data))
      } else {
        return condition.rules.some(rule => evaluateConditionRule(rule, data))
      }
    }

    return true
  }

  const findNextVisibleStepIndex = (startIndex: number, direction: 'forward' | 'backward'): number => {
    if (direction === 'forward') {
      for (let i = startIndex + 1; i < steps.value.length; i++) {
        if (shouldStepBeVisible(steps.value[i])) {
          return i
        }
      }
    } else {
      for (let i = startIndex - 1; i >= 0; i--) {
        if (shouldStepBeVisible(steps.value[i])) {
          return i
        }
      }
    }
    return -1
  }

  const loadTasteConfig = async (config: TasteConfig) => {
    tasteModal.value.config = config
    tasteModal.value.results = null
    // sessionId теперь будет создаваться при запуске сессии свайпов через API
    tasteModal.value.sessionId = null

    // Пытаемся восстановить сохранённый прогресс
    const saved = loadTasteProgress(config.id)
    if (saved && Object.keys(saved.tasteData).length > 0) {
      tasteModal.value.tasteData = saved.tasteData
      // Проверяем, что сохранённый индекс валиден
      if (saved.stepIndex >= 0 && saved.stepIndex < (config.steps?.length ?? 0)) {
        tasteModal.value.currentStepIndex = saved.stepIndex
      } else {
        const firstVisibleIndex = findNextVisibleStepIndex(-1, 'forward')
        tasteModal.value.currentStepIndex = firstVisibleIndex >= 0 ? firstVisibleIndex : 0
      }
    } else {
      tasteModal.value.tasteData = {}
      // Находим первый видимый шаг
      const firstVisibleIndex = findNextVisibleStepIndex(-1, 'forward')
      tasteModal.value.currentStepIndex = firstVisibleIndex >= 0 ? firstVisibleIndex : 0
    }
  }

  const validateCurrentStep = (): boolean => {
    const step = currentStep.value
    if (!step) return false

    if (step.required === false) return true

    if (step.type === 'gender') {
      return tasteModal.value.tasteData.gender !== null && tasteModal.value.tasteData.gender !== undefined
    }

    if (step.type === 'brand_selection') {
      const value = tasteModal.value.tasteData[step.id]
      // Если не требуется, можно пропустить даже без выбора
      if (!step.required) return true
      // Если требуется, проверяем что есть хотя бы один выбранный бренд
      return Array.isArray(value) && value.length > 0
    }

    if (step.type === 'style_selection') {
      const value = tasteModal.value.tasteData[step.id]
      // Если не требуется, можно пропустить даже без выбора
      if (!step.required) return true
      // Если требуется, проверяем что есть хотя бы один выбранный стиль
      return Array.isArray(value) && value.length > 0
    }

    if (step.type === 'color_selection') {
      const value = tasteModal.value.tasteData[step.id]
      // Если не требуется, можно пропустить даже без выбора
      if (!step.required) return true
      // Если требуется, проверяем что есть хотя бы один выбранный цвет
      return Array.isArray(value) && value.length > 0
    }

    if (step.type === 'heel_frequency') {
      const value = tasteModal.value.tasteData[step.id]
      return value !== null && value !== undefined && value !== ''
    }

    if (step.type === 'tinder_like') {
      // Для tinder_like проверяем, что все карточки были обработаны
      // Проверяем сохраненный индекс карточки в tasteData
      const stepKey = `${step.id}_cardIndex`
      const currentCardIndex = tasteModal.value.tasteData[stepKey]
      const cardsCount = step.cardsCount

      // Если required === false, можно пропустить даже без обработки всех карточек
      if (!step.required) return true

      // Проверяем, что currentCardIndex >= cardsCount (все карточки обработаны)
      return typeof currentCardIndex === 'number' && currentCardIndex >= cardsCount
    }

    if (step.type === 'choice') {
      const value = tasteModal.value.tasteData[step.id]
      // Если не требуется, можно пропустить
      if (!step.required) return true
      // Для множественного выбора проверяем, что есть хотя бы один выбранный элемент
      if (step.multiSelect) {
        return Array.isArray(value) && value.length > 0
      }
      // Для одиночного выбора проверяем, что значение не null/undefined
      return value !== null && value !== undefined
    }

    return true
  }

  const showTasteModal = async () => {
    tasteModal.value.isShow = true
    tasteModal.value.isLoadingConfig = true

    try {
      const config = await fetchTasteConfig()
      await loadTasteConfig(config)
    } catch (error) {
      console.error('Error loading taste config:', error)
      // Можно показать ошибку пользователю или использовать дефолтную конфигурацию
    } finally {
      tasteModal.value.isLoadingConfig = false
    }
  }

  const showTasteModalWithResume = async (configId: string) => {
    tasteModal.value.isShow = true
    tasteModal.value.isLoadingConfig = true

    try {
      const config = await fetchTasteConfigWithResume(configId)
      await loadTasteConfig(config)
    } catch (error) {
      console.error('Error loading taste config with resume:', error)
      // Fallback to regular config loading
      try {
        const config = await fetchTasteConfig()
        await loadTasteConfig(config)
      } catch (fallbackError) {
        console.error('Error loading fallback taste config:', fallbackError)
      }
    } finally {
      tasteModal.value.isLoadingConfig = false
    }
  }

  /**
   * Both profile entry points show a single tinder_like step. Its copy and card
   * count are server-owned, so the step is taken from GET /taste/config rather
   * than duplicated here; the modal opens in a loading state meanwhile.
   */
  const buildTinderLikeConfig = async (configTitle: string): Promise<TasteConfig | null> => {
    try {
      const config = await fetchTasteConfig()
      const step = config.steps?.find((candidate) => candidate.type === 'tinder_like')

      if (!step) {
        console.error('No tinder_like step in taste config')
        return null
      }

      return { id: config.id, title: configTitle, steps: [step] }
    } catch (error) {
      // Callers are fire-and-forget; swallowing here keeps a config failure from
      // becoming an unhandled rejection with the modal stuck loading.
      console.error('Failed to load taste config for tinder_like step:', error)
      return null
    }
  }

  const showTasteModalWithStyleTinderLike = async (styleId: number, gender?: 'male' | 'female') => {
    tasteModal.value.isShow = true
    tasteModal.value.isLoadingConfig = true

    const customConfig = await buildTinderLikeConfig('Уточнение стиля')
    tasteModal.value.isLoadingConfig = false

    if (!customConfig) {
      tasteModal.value.isShow = false
      return
    }

    loadTasteConfig(customConfig)

    // Устанавливаем style ID в tasteData для использования в TinderLikeStep
    tasteModal.value.tasteData['style-evaluation-choice'] = [styleId]

    // Устанавливаем gender для корректной фильтрации карточек
    if (gender) {
      tasteModal.value.tasteData['gender'] = gender
    }
  }

  const showTasteModalWithTinderLikeOnly = async (profileData: UserProfileDataResponse) => {
    // Извлекаем styleIds из taste.styles (преобразуем строки в числа)
    const styleIds = profileData.taste.styles.map(style => parseInt(style.styleId, 10)).filter(id => !isNaN(id))

    if (styleIds.length === 0) {
      console.error('No valid style IDs found in profile data')
      return
    }

    // Извлекаем gender из user.gender (проверяем, что это 'male' или 'female')
    const gender = profileData.user.gender
    const validGender = gender === 'male' || gender === 'female' ? gender : null

    tasteModal.value.isShow = true
    tasteModal.value.isLoadingConfig = true

    const customConfig = await buildTinderLikeConfig('Уточнение стилей')
    tasteModal.value.isLoadingConfig = false

    if (!customConfig) {
      tasteModal.value.isShow = false
      return
    }

    loadTasteConfig(customConfig)

    // Устанавливаем данные в tasteData для использования в TinderLikeStep
    tasteModal.value.tasteData['style-evaluation-choice'] = styleIds
    if (validGender) {
      tasteModal.value.tasteData['gender'] = validGender
    }
  }

  const hideTasteModal = async () => {
    // Проверяем, есть ли данные для отправки перед закрытием
    const hasDataToSubmit =
      tasteModal.value.config &&
      Object.keys(tasteModal.value.tasteData).length > 0 &&
      // Не отправляем, если пользователь только на welcome шаге
      currentStep.value?.type !== 'welcome'

    if (hasDataToSubmit) {
      // Сохраняем прогресс в localStorage для возможности продолжить позже
      saveTasteProgress(
        tasteModal.value.config!.id,
        tasteModal.value.tasteData,
        tasteModal.value.currentStepIndex
      )

      // Вызываем submitTasteSurvey асинхронно, но не блокируем закрытие модального окна
      submitTasteSurvey().catch((error) => {
        console.error('Error auto-submitting taste survey on close:', error)
      })
    }

    tasteModal.value.isShow = false
    // Очищаем sessionId при закрытии модального окна
    tasteModal.value.sessionId = null
    // Сбрасываем состояние статического шага
    tasteModal.value.isShowingLaterStep = false
    tasteModal.value.previousStepIndex = null
  }

  const showLaterStep = () => {
    // Сохраняем текущий индекс шага для возврата
    tasteModal.value.previousStepIndex = tasteModal.value.currentStepIndex
    tasteModal.value.isShowingLaterStep = true
  }

  const hideLaterStep = () => {
    tasteModal.value.isShowingLaterStep = false
    // Возвращаемся на предыдущий шаг, если он был сохранен
    if (tasteModal.value.previousStepIndex !== null) {
      tasteModal.value.currentStepIndex = tasteModal.value.previousStepIndex
      tasteModal.value.previousStepIndex = null
    }
  }

  const nextStep = () => {
    if (!validateCurrentStep()) {
      return
    }

    const nextIndex = findNextVisibleStepIndex(tasteModal.value.currentStepIndex, 'forward')
    if (nextIndex >= 0) {
      tasteModal.value.currentStepIndex = nextIndex
    }
  }

  const prevStep = () => {
    const prevIndex = findNextVisibleStepIndex(tasteModal.value.currentStepIndex, 'backward')
    if (prevIndex >= 0) {
      tasteModal.value.currentStepIndex = prevIndex
    }
  }

  const goToStep = (stepIndex: number) => {
    if (stepIndex >= 0 && stepIndex < totalSteps.value) {
      const step = steps.value[stepIndex]
      // Если шаг не видим, находим ближайший видимый
      if (step && !shouldStepBeVisible(step)) {
        const visibleIndex = findNextVisibleStepIndex(stepIndex, 'forward')
        if (visibleIndex >= 0) {
          tasteModal.value.currentStepIndex = visibleIndex
        } else {
          // Если вперед нет видимых, ищем назад
          const visibleIndexBack = findNextVisibleStepIndex(stepIndex, 'backward')
          if (visibleIndexBack >= 0) {
            tasteModal.value.currentStepIndex = visibleIndexBack
          }
        }
      } else {
        tasteModal.value.currentStepIndex = stepIndex
      }
    }
  }

  const goToStepById = (stepId: string) => {
    const index = getStepIndexById(stepId)
    if (index >= 0) {
      goToStep(index)
    }
  }

  const updateTasteData = (key: string, value: any) => {
    tasteModal.value.tasteData[key] = value

    // Если текущий шаг стал невидимым из-за изменения данных, переходим к следующему видимому
    const currentStepValue = currentStep.value
    if (currentStepValue && !shouldStepBeVisible(currentStepValue)) {
      const nextVisibleIndex = findNextVisibleStepIndex(tasteModal.value.currentStepIndex, 'forward')
      if (nextVisibleIndex >= 0) {
        tasteModal.value.currentStepIndex = nextVisibleIndex
      } else {
        // Если вперед нет видимых, ищем назад
        const prevVisibleIndex = findNextVisibleStepIndex(tasteModal.value.currentStepIndex, 'backward')
        if (prevVisibleIndex >= 0) {
          tasteModal.value.currentStepIndex = prevVisibleIndex
        }
      }
    }
  }

  const submitTasteSurvey = async () => {
    tasteModal.value.isLoading = true
    try {
      const config = tasteModal.value.config
      if (!config) {
        throw new Error('Taste config is not loaded')
      }

      // Преобразуем ответы в формат с integer ID
      const { transformTasteAnswersForSubmission } = await import('../utils/taste-submit')
      const transformedAnswers = transformTasteAnswersForSubmission(config, tasteModal.value.tasteData)

      // Формируем данные для отправки на бекенд
      const submitData: TasteSubmitRequest = {
        tasteConfigId: config.id,
        answers: transformedAnswers,
        metadata: {
          completedAt: new Date().toISOString(),
          deviceType: typeof window !== 'undefined' && window.innerWidth <= 768 ? 'mobile' : 'desktop',
          language: 'ru'
        }
      }

      // Отправляем данные на бекенд
      // submitTasteAnswers теперь возвращает stylePreferences из ответа
      const results = await submitTasteAnswers(submitData)

      // Сохраняем результаты в store
      tasteModal.value.results = results

      // Опрос успешно отправлен — очищаем сохранённый прогресс
      clearTasteProgress()

      // Invalidate profile data cache so the profile page shows updated data
      queryCache.invalidate(['user-profile', 'profile-data'])
    } catch (error) {
      console.error('Error submitting taste survey:', error)
      // В случае ошибки используем мок данные для демонстрации
      tasteModal.value.results = {
        favoriteStyles: [
          {
            name: 'Классический',
            image: '/img/styles/classic.jpg'
          },
          {
            name: 'Спорт',
            image: '/img/styles/sport.jpg'
          },
          {
            name: 'Кэжуал',
            image: '/img/styles/casual.jpg'
          }
        ],
        favoriteColors: [
          { name: 'Черный', hex: '#000000' },
          { name: 'Белый', hex: '#FFFFFF' },
          { name: 'Синий', hex: '#0000FF' },
          { name: 'Красный', hex: '#FF0000' }
        ],
        favoriteBrands: (tasteModal.value.tasteData.favoriteBrands || []).map((brand: string) => ({
          name: brand,
          logo: `/img/brands/${brand.toLowerCase().replace(/\s+/g, '-')}-logo.png`
        }))
      }
    } finally {
      tasteModal.value.isLoading = false
    }
  }

  // Методы для работы с результатами (используются в компонентах шагов)
  const addFavoriteStyle = (style: { name: string; image: string }) => {
    if (!tasteModal.value.results) {
      tasteModal.value.results = {
        favoriteStyles: [],
        favoriteColors: [],
        favoriteBrands: []
      }
    }
    if (!tasteModal.value.results.favoriteStyles.some((s) => s.name === style.name)) {
      tasteModal.value.results.favoriteStyles.push(style)
    }
  }

  const addFavoriteColor = (color: { name: string; hex: string }) => {
    if (!tasteModal.value.results) {
      tasteModal.value.results = {
        favoriteStyles: [],
        favoriteColors: [],
        favoriteBrands: []
      }
    }
    if (!tasteModal.value.results.favoriteColors.some((c) => c.name === color.name)) {
      tasteModal.value.results.favoriteColors.push(color)
    }
  }

  const addFavoriteBrand = (brand: { name: string; logo: string }) => {
    if (!tasteModal.value.results) {
      tasteModal.value.results = {
        favoriteStyles: [],
        favoriteColors: [],
        favoriteBrands: []
      }
    }
    if (!tasteModal.value.results.favoriteBrands.some((b) => b.name === brand.name)) {
      tasteModal.value.results.favoriteBrands.push(brand)
    }
  }

  return {
    tasteModal,
    steps,
    currentStep,
    totalSteps,
    getCurrentStep,
    getStepById,
    getStepIndexById,
    shouldStepBeVisible,
    findNextVisibleStepIndex,
    loadTasteConfig,
    validateCurrentStep,
    showTasteModal,
    showTasteModalWithResume,
    showTasteModalWithStyleTinderLike,
    showTasteModalWithTinderLikeOnly,
    hideTasteModal,
    nextStep,
    prevStep,
    goToStep,
    goToStepById,
    updateTasteData,
    submitTasteSurvey,
    addFavoriteStyle,
    addFavoriteColor,
    addFavoriteBrand,
    showLaterStep,
    hideLaterStep
  }
})

export const useTasteClarificationModalStore = useTasteClarificationModal
